import { useQuery } from "@tanstack/react-query";
import { v5 as uuidv5 } from "uuid";

import { UUID_NAMESPACE } from "#/config/definitions";
import { defaultGreeks } from "#/config/greeks";
import { defaultMarket } from "#/config/markets";
import { useEoa } from "#/store/wallet/eoa/eoa";
import { type Market } from "#/types/markets";
import { type Option } from "#/types/options";

import { calculateUserRisk } from "../kyan.service";
import { type SuccessfulUserPortfolioResponse } from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";
import { useNettedUserPortfolio } from "./useNettedUserPortfolio";

const defaultUserRisk: SuccessfulUserPortfolioResponse = {
  pair: defaultMarket.pair,
  greeks: defaultGreeks,
  im: 0,
  mm: 0,
  matrix_risk: 0,
  delta_risk: 0,
  roll_risk: 0,
  future_settlement_projections: {},
};

interface UseCalculateUserRiskInput {
  size: string;
  instrument: string;
  isBuy: boolean;
  limitPrice?: string;
  isEnabled?: boolean;
}

interface UseCalculateUserRiskProps {
  inputs: UseCalculateUserRiskInput[];
  refetchInterval?: number | false;
  market?: Market;
  option?: Option | null;
  throttleFrequencyMs?: number;
}

export const useCalculateUserRisk = ({
  inputs,
  refetchInterval = false,
  option,
  market,
  throttleFrequencyMs,
}: UseCalculateUserRiskProps): SuccessfulUserPortfolioResponse & {
  isLoading: boolean;
} => {
  const userPortfolio = useNettedUserPortfolio({
    option,
    market,
    inputs,
    throttleFrequencyMs,
  });
  const { isEoaConnected } = useEoa();

  const isEnabled =
    isEoaConnected &&
    inputs.every((input) => input.isEnabled !== false) &&
    inputs.every((input) => Boolean(input.size)) &&
    inputs.every(
      (input) => Boolean(input.limitPrice) || input.limitPrice === undefined,
    ) &&
    inputs.every((input) => Boolean(input.instrument)) &&
    Boolean(userPortfolio.positions.length);

  const { data: userRisk = defaultUserRisk, isLoading } = useQuery({
    queryKey: [
      "calculateUserRisk",
      uuidv5(JSON.stringify([userPortfolio]), UUID_NAMESPACE),
    ],
    queryFn: () =>
      defaultApiResponseHandler({
        serviceName: "Calculate User Risk",
        payload: [userPortfolio],
        service: calculateUserRisk,
      }),
    enabled: isEnabled,
    select: (userRiskData): SuccessfulUserPortfolioResponse | undefined => {
      if (userRiskData?.[0]?.status === "success") {
        return userRiskData[0].data as SuccessfulUserPortfolioResponse;
      }
      return undefined;
    },
    refetchInterval,
  });

  return {
    ...userRisk,
    isLoading,
  };
};
